import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  useNavigate,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { trackClick } from "@/lib/tracking";
import {
  BOIDE_AUTH_CHANGED_EVENT,
  autoBindStoredReferral,
  getBoideToken,
  setStoredReferral,
  setStoredTrack,
} from "@/lib/boide-api";
import { useReducedEffects } from "@/hooks/use-reduced-effects";

import appCss from "../styles.css?url";
import logoIconUrl from "@/assets/logo-icon.png";
import { TopNav } from "@/components/top-nav";
import { BottomNav } from "@/components/bottom-nav";

import { CosmicBackground } from "@/components/cosmic-background";
import { MaintenancePage } from "@/components/maintenance-page";
import { FeatureMaintenanceGate } from "@/components/feature-maintenance-banner";
import { ReferralPromptDialog } from "@/components/affiliate/referral-prompt-dialog";
import { loadUiMode } from "@/lib/event-config";
import { loadMaintenance } from "@/lib/maintenance-config";
import { useMaintenance } from "@/hooks/use-maintenance";
import { TourProvider } from "@/components/tour/tour-provider";
import { InAppBrowserWarning } from "@/components/in-app-browser-warning";
import { PalmJobWatcher } from "@/components/palm/palm-job-watcher";
import { AnnouncementPopup } from "@/components/announcement-popup";
import { QuickTopupDialog } from "@/components/quick-topup-dialog";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  QUICK_TOPUP_EVENT,
  type QuickTopupEventDetail,
  type QuickTopupOffer,
} from "@/lib/quick-topup";
import { QuickTopupCountdownPill } from "@/components/quick-topup-countdown";
import { AppUpdatePrompt } from "@/components/app-update-prompt";
import {
  QUICK_TOPUP_OFFER_QUERY_KEY,
  QUICK_TOPUP_OFFER_STALE_TIME,
  fetchStampedQuickTopupOffer,
} from "@/lib/quick-topup-query";
import { BUSINESS_INFO } from "@/lib/business-info";
// Side-effect import: nạp token từ ?dev_token= khi chạy dev (no-op ở production).
import "@/lib/dev-token-bridge";
import { installApiFetchGuard } from "@/lib/api-fetch-guard";

// 🔴 Chạy ở MODULE SCOPE, trước mọi lượt fetch của app (ui-config, maintenance…):
// backend bật rate-limit thì API công khai thiếu X-Anon-Token là 401. Xem
// `lib/api-fetch-guard.ts`. No-op phía server.
installApiFetchGuard();

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const GTM_ID = import.meta.env.VITE_GTM_ID || "GTM-KQDGVQF4";

// Google Fonts inject bằng script SAU first paint (rAF + setTimeout) thay vì <link rel="stylesheet">
// chặn render: display=swap nên chữ hiện ngay bằng font hệ thống, webfont swap vào sau.
// Lưu ý: không preload/inject sớm — request font priority VeryHigh hoàn thành trước FCP
// sẽ bị Lighthouse (lantern) tính vào đường găng render, đội FCP mô phỏng lên ~2-3s.
const FONT_CSS_URL =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Inter:wght@400;500;600&family=Lora:wght@600&family=Be+Vietnam+Pro:wght@400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap&subset=vietnamese";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-cosmic px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl text-gold-gradient">404</h1>
        <h2 className="mt-4 font-serif-soft text-xl">Thiên cơ chưa lộ</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Trang bạn tìm không tồn tại trong vũ trụ này.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center justify-center rounded-full bg-gold-gradient px-6 py-2 text-sm font-medium text-primary-foreground shadow-gold"
        >
          Về trang chủ
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-cosmic px-4">
      <div className="max-w-md text-center">
        <h1 className="font-serif-soft text-xl">Có biến cố xảy ra</h1>
        <p className="mt-2 text-sm text-muted-foreground">Xin hãy thử lại sau giây lát.</p>
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="mt-6 inline-flex rounded-full bg-gold-gradient px-6 py-2 text-sm font-medium text-primary-foreground"
        >
          Thử lại
        </button>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { title: "Bói Đê — Huyền học Đông Phương" },
      {
        name: "description",
        content:
          "Bói Đê — nền tảng huyền học Đông phương cao cấp: gieo quẻ Kinh Dịch, lập lá số Tử Vi và luận giải vận mệnh bằng AI.",
      },
      { property: "og:site_name", content: "Bói Đê" },
      { property: "og:title", content: "Bói Đê — Huyền học Đông Phương" },
      { name: "twitter:title", content: "Bói Đê — Huyền học Đông Phương" },
      {
        property: "og:description",
        content:
          "Bói Đê — nền tảng huyền học Đông phương cao cấp: gieo quẻ Kinh Dịch, lập lá số Tử Vi và luận giải vận mệnh bằng AI.",
      },
      {
        name: "twitter:description",
        content:
          "Bói Đê — nền tảng huyền học Đông phương cao cấp: gieo quẻ Kinh Dịch, lập lá số Tử Vi và luận giải vận mệnh bằng AI.",
      },
      { property: "og:image", content: "https://boide.online/og-cover.png" },
      { property: "og:image:secure_url", content: "https://boide.online/og-cover.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: "Bói Đê — gieo quẻ Kinh Dịch, lập lá số Tử Vi và Bát Tự",
      },
      { name: "twitter:image", content: "https://boide.online/og-cover.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { name: "theme-color", content: "#241b30" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      /**
       * KHÔNG dùng "black-translucent": trên iOS standalone nó cho web vẽ từ y=0 (đè
       * status bar) nhưng vẫn trừ đúng chiều cao status bar khỏi innerHeight
       * (874 → 812 trên iPhone 16 Pro), để lại dải trống 62px ở đáy mà `bottom: 0`
       * không chạm tới. Với "default", iOS đặt viewport ngay dưới status bar và phủ
       * hết tới đáy màn hình nên BottomNav dán đúng đáy.
       */
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "Bói Đê" },
    ],
    links: [
      { rel: "icon", type: "image/png", href: logoIconUrl },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/pwa/apple-touch-icon.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
    ],
    scripts: [
      {
        type: "text/javascript",
        children: `(function(){var done=false;function add(){if(done)return;done=true;var l=document.createElement('link');l.rel='stylesheet';l.href='${FONT_CSS_URL}';document.head.appendChild(l);}try{new PerformanceObserver(function(list,obs){if(list.getEntriesByName('first-contentful-paint').length){obs.disconnect();setTimeout(add,250);}}).observe({type:'paint',buffered:true});}catch(e){}setTimeout(add,3000);})();`,
      },
      {
        type: "text/javascript",
        children: `(function() {
          try {
            var theme = localStorage.getItem('theme') || 'light';
            if (theme === 'light') {
              document.documentElement.classList.remove('dark');
              document.documentElement.classList.add('light');
            } else {
              document.documentElement.classList.remove('light');
              document.documentElement.classList.add('dark');
            }
          } catch (e) {}
        })();`,
      },
      {
        type: "text/javascript",
        children: `(function(w,d,s,l,i){w[l]=w[l]||[];function load(){if(w.__gtmLoaded)return;w.__gtmLoaded=true;w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);}var e=['scroll','mousemove','touchstart','keydown','click'];function on(){e.forEach(function(x){w.removeEventListener(x,on)});load();}e.forEach(function(x){w.addEventListener(x,on,{passive:true,once:true})});function idle(){setTimeout(load,3500);}if(d.readyState==='complete'){idle();}else{w.addEventListener('load',idle);}})(window,document,'script','dataLayer','${GTM_ID}');`,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: BUSINESS_INFO.name,
          url: "https://boide.online",
          logo: "https://boide.online/logo-icon.png",
          ...(BUSINESS_INFO.website ? { sameAs: [`https://${BUSINESS_INFO.website}`] } : {}),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Bói Đê",
          url: "https://boide.online",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="vi" suppressHydrationWarning>
      <head>
        <HeadContent />
        {/* Fallback khi tắt JS — script inject font ở trên không chạy được */}
        <noscript>
          <link rel="stylesheet" href={FONT_CSS_URL} />
        </noscript>
      </head>
      <body>
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const isPending = useRouterState({ select: (s) => s.status === "pending" });
  const location = useRouterState({ select: (s) => s.location });
  const navigate = useNavigate();
  const reduceEffects = useReducedEffects();
  const [isMounted, setIsMounted] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupReason, setTopupReason] = useState<string>();
  const [topupOffer, setTopupOffer] = useState<QuickTopupOffer>();
  // Offer đang chạy trong ngày — nuôi pill countdown nổi khi popup đã đóng.
  const [activeOffer, setActiveOffer] = useState<QuickTopupOffer>();

  // 🔴 Bảo trì nay là CỜ RUNTIME do admin bật ở /admin/maintenance (migration v118),
  // không còn là biến build-time `VITE_MAINTENANCE_MODE` — biến ấy chỉ còn là đường
  // đè cho ca chính backend đã chết hẳn (xem `maintenance-config`).
  const maintenance = useMaintenance();
  const isMaintenance = maintenance.enabled;
  const isRenderRoute = location.pathname.startsWith("/render/");

  const refreshActiveOffer = useCallback(() => {
    if (!getBoideToken()) {
      setActiveOffer(undefined);
      return;
    }
    // Cùng khóa với trang chủ → react-query gộp làm một request.
    void queryClient
      .fetchQuery({
        queryKey: QUICK_TOPUP_OFFER_QUERY_KEY,
        queryFn: fetchStampedQuickTopupOffer,
        staleTime: QUICK_TOPUP_OFFER_STALE_TIME,
      })
      .then((offer) => setActiveOffer(offer ?? undefined))
      .catch(() => setActiveOffer(undefined));
  }, [queryClient]);

  // Đổi tài khoản: bỏ MỌI dữ liệu đã cache của phiên trước. Bắt buộc từ khi query có
  // staleTime mặc định — thiếu bước này, đăng xuất rồi vào tài khoản khác trong vòng
  // staleTime là thấy streak/hộp thư/offer của người trước.
  const handleAuthChanged = useCallback(() => {
    void queryClient.resetQueries();
    refreshActiveOffer();
  }, [queryClient, refreshActiveOffer]);

  const handleTopupOfferChange = useCallback((offer?: QuickTopupOffer) => {
    setTopupOffer(offer);
    setActiveOffer(offer);
  }, []);

  useEffect(() => {
    const handleQuickTopup = (event: Event) => {
      const detail = (event as CustomEvent<QuickTopupEventDetail>).detail;
      setTopupReason(detail?.reason);
      setTopupOffer(detail?.offer);
      if (detail?.offer) setActiveOffer(detail.offer);
      setTopupOpen(true);
    };
    window.addEventListener(QUICK_TOPUP_EVENT, handleQuickTopup);
    return () => window.removeEventListener(QUICK_TOPUP_EVENT, handleQuickTopup);
  }, []);

  // Sau F5 / đăng nhập lại vẫn thấy countdown nếu offer trong ngày còn chạy.
  useEffect(() => {
    if (isMaintenance || isRenderRoute) return;
    refreshActiveOffer();
    window.addEventListener(BOIDE_AUTH_CHANGED_EVENT, handleAuthChanged);
    return () => window.removeEventListener(BOIDE_AUTH_CHANGED_EVENT, handleAuthChanged);
  }, [isMaintenance, isRenderRoute, refreshActiveOffer, handleAuthChanged]);

  // Loại giao diện (thường / sự kiện) do admin chọn — hỏi máy chủ một lần mỗi lượt
  // vào web. KHÔNG cần auth và KHÔNG chặn render: store đã được seed sẵn từ
  // localStorage nên trang vẽ ngay bằng giá trị của lần trước, câu trả lời về thì
  // `useEventUI()` tự cập nhật.
  //
  // ⚠️ Route `/render/*` cố ý bỏ qua: nó chụp ảnh share trong iframe, phải giữ đúng
  // một bố cục cố định và không được phụ thuộc vào một lời gọi mạng.
  useEffect(() => {
    if (isMaintenance || isRenderRoute) return;
    void loadUiMode();
  }, [isMaintenance, isRenderRoute]);

  // Trạng thái bảo trì: hỏi một lần lúc vào web, rồi hỏi lại theo nhịp.
  //
  // 🔴 KHÔNG dừng hỏi khi đang bảo trì — đó chính là lúc phải hỏi tiếp, nếu không
  // người dùng kẹt vĩnh viễn ở màn bảo trì sau khi hệ thống đã mở lại và chỉ thoát
  // ra được bằng cách tự tải lại trang.
  // ⚠️ Nhịp NGẮN HƠN khi đang bảo trì (15s so với 2 phút): lúc bình thường thì mỗi
  // lượt gọi API đã tự bắt 503 rồi, nhịp này chỉ để bắt ca người dùng mở sẵn trang
  // mà không bấm gì; còn lúc đang bảo trì thì nó là đường DUY NHẤT để họ thoát ra.
  useEffect(() => {
    if (isRenderRoute) return;
    void loadMaintenance();
    const period = isMaintenance ? 15_000 : 120_000;
    const timer = window.setInterval(() => void loadMaintenance(), period);
    return () => window.clearInterval(timer);
  }, [isMaintenance, isRenderRoute]);

  useEffect(() => {
    setIsMounted(true);
    if (isMaintenance) return;
    const search = location.search as Record<string, unknown>;
    // Bắt mã giới thiệu affiliate (?ref=) + nhãn tracking (?track=) → lưu localStorage (60 ngày).
    // Link KOL cũ (?platform=&kol=&campaign=) gộp thành track="platform:kol:campaign".
    const referralCode = typeof search.ref === "string" && search.ref ? search.ref : undefined;
    const platform = typeof search.platform === "string" ? search.platform : undefined;
    const kol = typeof search.kol === "string" ? search.kol : undefined;
    const campaign = typeof search.campaign === "string" ? search.campaign : undefined;
    let track = typeof search.track === "string" && search.track ? search.track : undefined;
    if (!track && platform && kol && campaign) {
      track = `${platform}:${kol}:${campaign}`;
    }
    if (referralCode) {
      setStoredReferral(referralCode);
      // Đã đăng nhập sẵn mà bấm link giới thiệu ⇒ gắn luôn (set-once ở BE), không đợi
      // lượt nạp tiền. Chưa đăng nhập thì `/login` gắn ngay sau khi có token.
      if (getBoideToken()) {
        void autoBindStoredReferral().then((bound) => {
          if (bound) toast.success(`Đã gắn mã giới thiệu ${bound}`);
        });
      }
    }
    if (track) {
      setStoredTrack(track);
    }
    if (referralCode || track) {
      // Fire-and-forget từ browser — không chặn render, BE ghi đúng IP người click.
      trackClick({ referral_code: referralCode, track });
    }
    if (search.platform || search.kol || search.campaign || search.ref || search.track) {
      navigate({
        to: location.pathname,
        search: (prev: Record<string, unknown>) => {
          const { platform: _p, kol: _k, campaign: _c, ref: _r, track: _t, ...rest } = prev;
          return rest;
        },
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMaintenance) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page_path: location.href,
      page_title: document.title,
    });
  }, [location.href]);

  if (isMaintenance) {
    return <MaintenancePage message={maintenance.message} eta={maintenance.eta} />;
  }

  // Route render ảnh share (/render/*) — bare, KHÔNG nav/nền/GTM để chụp board
  // sạch trong iframe (viewport cố định 1280px → luôn layout desktop).
  if (location.pathname.startsWith("/render/")) {
    return (
      <QueryClientProvider client={queryClient}>
        <Outlet />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <CosmicBackground />
      <TourProvider>
        <QuickTopupDialog
          open={topupOpen}
          onOpenChange={(open) => {
            setTopupOpen(open);
            if (!open) refreshActiveOffer();
          }}
          onOfferChange={handleTopupOfferChange}
          reason={topupReason}
          initialOffer={topupOffer}
        />
        <QuickTopupCountdownPill
          offer={activeOffer}
          hidden={topupOpen}
          onClick={() => {
            setTopupReason("Ưu đãi nạp nhanh đang chờ bạn — nạp trước khi hết giờ nhé.");
            setTopupOffer(activeOffer);
            setTopupOpen(true);
          }}
        />
        <div
          className={`flex h-dvh w-full flex-col overflow-hidden ${reduceEffects ? "reduce-effects" : ""}`}
        >
          {/* Glowing top progress bar for instant navigation feedback */}
          <div
            className="fixed top-0 left-0 right-0 z-50 h-[3px] bg-gradient-to-r from-amber-400 via-yellow-200 to-amber-400 shadow-[0_0_12px_oklch(0.82_0.14_85/0.8)] origin-left transition-all duration-500 ease-out"
            style={{
              transform: isMounted && isPending ? "scaleX(1)" : "scaleX(0)",
              opacity: isMounted && isPending ? 1 : 0,
            }}
          />
          <TopNav />
          {/* Bảo trì RIÊNG tính năng của trang đang mở (BE v121): banner, hoặc thay cả
              trang bằng màn bảo trì khi admin chọn "Chặn cả xem". */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <FeatureMaintenanceGate pathname={location.pathname}>
              <Outlet />
            </FeatureMaintenanceGate>
          </div>
          <BottomNav />
        </div>
        <InAppBrowserWarning />
        {/* Không vẽ gì — theo dõi lượt bói chỉ tay chạy nền để báo toast được ở MỌI
            trang. Phải nằm ở đây chứ không phải trong trang chỉ tay: rời trang là
            trang ấy unmount, và người xem được mời rời trang trong lúc chờ. */}
        <PalmJobWatcher />
        {/* Popup thông báo do admin tạo. Cũng phải ở đây: người dùng có thể mở app
            ở bất kỳ trang nào, mà điều hướng trong SPA không tạo mount mới. */}
        <AnnouncementPopup />
        {/* Mời nhập mã giới thiệu sau lượt đăng nhập (chỉ tài khoản chưa gắn mã). */}
        <ReferralPromptDialog pathname={location.pathname} />
        {/* PWA: báo "Có bản mới" khi đã deploy bản khác bản đang chạy. */}
        <AppUpdatePrompt />
        {/* Không có Toaster thì mọi toast.error/success trong app đều im lặng. */}
        <Toaster position="top-center" richColors closeButton />
      </TourProvider>
    </QueryClientProvider>
  );
}
